import React, { useEffect } from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {FormInput} from '../../../../../../components/shared/form-controls/form-input';
import {FormCheckbox} from '../../../../../../components/shared/form-controls/form-checkbox';
import {useAppDispatch, useAppSelector} from '../../../../../../hooks/hooks';
import {addAbsenceType} from '../../../../../../features/types';
import {AbsenceBehavior, TiminatorAbsenceType} from '../../../../../../features/types/types';
import { useIntl } from 'react-intl';
import { Lx } from '../../../../../../i18n/consts';
import BaseForm from '../../../../../../components/shared/form-controls/base-form';
import { useNavigate } from 'react-router-dom';
import { NAVIGATION_ROUTES } from '../../../../../../routes/NavigationRoute';
import { canManageAbsenceTypes } from '../../../../../../features/profile/entityPermissions';
import { DropdownField } from '../../../../../../components/shared/nextgen/dropdown-field';

interface CreateForm {
    name: string;
    isDefault: boolean;
    isActive: boolean;
    behavior: { id: AbsenceBehavior, name: string };
}

export default function AbsenceTypeCreateForm() {
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const navigate = useNavigate();
    const profile = useAppSelector(store => store.profile);
    const canEditAbsenceTypes = canManageAbsenceTypes(profile);
    const behaviorOptions = [
        { id: AbsenceBehavior.None, name: 'None' },
        { id: AbsenceBehavior.Vacation, name: 'Use for vacation' },
        { id: AbsenceBehavior.LimitToPlannedHours, name: 'Use planned hours only' },
    ];

    useEffect(() => {
        if (!canEditAbsenceTypes) {
            navigate(NAVIGATION_ROUTES.ABSENCE_TYPES);
        }
    }, [canEditAbsenceTypes, navigate]);

    if (!canEditAbsenceTypes) {
        return null;
    }

    const initialValue: CreateForm = {
        name: '',
        isDefault: false,
        isActive: true,
        behavior: behaviorOptions[0]
    };

    const validationScheme: Yup.Schema<CreateForm> = Yup.object({
        name: Yup.string()
            .max(45, 'Must be 45 characters or less')
            .required(intl.formatMessage({ id: Lx.Form.NAME_REQUIRED_FORMAT }, { s: intl.formatMessage({ id: Lx.General.ABSENCE_TYPE }) })),
        isDefault: Yup.boolean().required(),
        isActive: Yup.boolean().required(),
        behavior: Yup.mixed<CreateForm['behavior']>().required(),
    })

    const save = async (values: CreateForm) => {
        const type: TiminatorAbsenceType = {
            id: 0,
            name: values.name,
            active: values.isActive,
            default: values.isDefault,
            behavior: values.behavior.id,
        }
        const createdType = await dispatch(addAbsenceType(type)).unwrap();
        navigate(NAVIGATION_ROUTES.ABSENCE_TYPES, {
            replace: true,
            state: {
                createdAbsenceType: createdType,
            },
        });
    }

    return <Formik
        initialValues={initialValue}
        validationSchema={validationScheme}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={save}>
        <Form>
            <BaseForm contentGap={24}
                formType='create'
                formHeader='create_absence_type'
                savingTitle='Absence type' 
                onClose={() => navigate(NAVIGATION_ROUTES.ABSENCE_TYPES)} 
                onAfterSave={() => undefined}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '500px' }}>
                    <FormInput name='name' label='Name' placeholder='Absence type' />
                    <DropdownField name='behavior' label='Behavior' items={behaviorOptions} required placeholder='' />
                    <fieldset>
                        <FormCheckbox name='isDefault' label='Default' />
                        <FormCheckbox name='isActive' label='Active'/>
                    </fieldset>
                </div>
            </BaseForm>
        </Form>
    </Formik>
}
